/*
  Разбор спорных поступлений - §3.10.2.

  Отдельным файлом от `StatementUpload.jsx` (18.09.2026): там загрузка файла,
  здесь работа человека с тем, что загрузка разнести не смогла. Две разные
  ответственности, и деление не формальное - загрузка происходит раз в месяц, а
  в этот список приходят каждый день.

  🔴 Наугад не разносим. Поступление, счёт которого система определить
  однозначно не смогла, остаётся человеку: это чужие деньги на чужом долге, и
  ошибка здесь уменьшает требование к настоящему должнику, а заметить её нечем.
  Если у системы есть догадка, кнопка названа номером счёта - человек видит, с чем
  соглашается. Если догадки нет, счёт выбирает человек сам (28.10.2026, аудит 5.3:
  раньше экран отсылал к карточке счёта, где платёж отнести некуда).

  С 21.10.2026 очередь режет СЕРВЕР (требование владельца: пагинация серверная):
  экран просит окно (`limit`, `offset`) и получает `{ items, total }`, панель общая.
  Список читает сам экран; загрузка выписки сообщает о новой очереди отметкой `stamp`.
*/

import { useCallback, useState } from 'react'

import { api } from '../api/client.js'
import { date, money } from '../api/format.js'
import { Empty, Loading, Notice } from './Common.jsx'
import PageBar from './PageBar.jsx'
import StatementAccountPick from './StatementAccountPick.jsx'
import usePagedList from './usePagedList.js'
import useReloadOn from './useReloadOn.js'

export default function StatementReview({ stamp = 0 }) {
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)
  // Поступление, к которому человек сам выбирает счёт (система его не предложила).
  const [picking, setPicking] = useState(null)
  const load = useCallback((window) => api.paymentsReview(window), [])
  // Хук - до раннего return: очередь приходит позже первого показа.
  const { rows, error: listError, reload, bar } = usePagedList(load)
  useReloadOn(stamp, reload)

  async function assign(paymentId, accountId) {
    setBusyId(paymentId)
    setError(null)
    try {
      await api.matchPayment(paymentId, accountId)
      reload()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusyId(null)
    }
  }

  if (!rows) {
    return listError ? <Notice error={listError} /> : <Loading what="Загружаем список разбора" />
  }

  return (
    <section className="section">
      <h2 className="section__title">Требуют вашего разбора</h2>
      {rows.length === 0 ? (
        <Empty title="Разбирать нечего">
          Здесь появятся поступления, счёт которых Арбитр ЖКХ не смог определить однозначно.
          Разнести их наугад нельзя: это чужие деньги на чужом долге.
        </Empty>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th className="table__num">Сумма</th>
                  <th>Плательщик</th>
                  <th>Почему не разнесено</th>
                  <th>Решение</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.payment_id}>
                    <td>{date(item.paid_on)}</td>
                    <td className="table__num" data-label="Сумма">
                      {money(item.amount)}
                    </td>
                    <td data-label="Плательщик">{item.payer ?? '-'}</td>
                    <td className="table__block" data-label="Почему не разнесено">
                      {item.explanation}
                      {item.purpose ? <div className="muted">{item.purpose}</div> : null}
                    </td>
                    <td className="table__block" data-label="Решение">
                      {item.suggested_account_id ? (
                        <button
                          type="button"
                          className="button button--quiet"
                          disabled={busyId === item.payment_id}
                          onClick={() => assign(item.payment_id, item.suggested_account_id)}
                        >
                          Это счёт {item.suggested_account_number}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="button button--quiet"
                          disabled={busyId === item.payment_id}
                          onClick={() => setPicking(item)}
                        >
                          Выбрать счёт
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PageBar {...bar} what="поступлений" />
          <Notice error={error} />
        </div>
      )}
      {picking ? (
        <StatementAccountPick payment={picking} onSaved={reload} onClose={() => setPicking(null)} />
      ) : null}
    </section>
  )
}
